import {
  Box,
  InputAdornment,
  Link as MuiLink,
  TableCell,
  TableRow,
  TextField,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";

import { supportApi } from "../client.js";
import { SupportErrorState } from "../components/SupportEmptyState.js";
import { PageLoader } from "@/components/ui/page-loader";
import {
  Pagination,
  paginationPropsFromMeta,
} from "../../../components/ui/pagination.js";
import { SupportTable } from "../components/SupportTable.js";

/**
 * Customer organisations indexed for the support team. Read-only —
 * orgs are owned by CRM, the support shell only surfaces them so an
 * agent on a ticket can pivot to "show me everything for this customer"
 * without leaving the support tab. Edits live in CRM.
 */
export function OrganizationsListPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? undefined;
  const page = params.get("page") ?? "1";
  const perPage = params.get("per_page") ?? "25";

  const orgsQuery = useQuery({
    queryKey: ["support", "orgs", { q, page, perPage }],
    queryFn: () => {
      const p: Record<string, string> = { page, per_page: perPage };
      if (q) p.q = q;
      return supportApi.listOrganizations(p);
    },
    staleTime: 15_000,
  });

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete("page");
    setParams(next, { replace: true });
  }

  return (
    <Box component="section" sx={{ display: "grid", gap: 2 }}>
      <SearchInput
        value={q ?? ""}
        onCommit={(v) => setParam("q", v || null)}
      />

      {orgsQuery.isLoading && <PageLoader />}
      {orgsQuery.isError && (
        <SupportErrorState
          error={orgsQuery.error}
          resourceLabel="organisations"
        />
      )}

      {orgsQuery.data && (
        <SupportTable
          head={
            <TableRow>
              <TableCell align="left">Name</TableCell>
              <TableCell align="left">Domain</TableCell>
              <TableCell align="left">Contacts</TableCell>
              <TableCell align="left">Open tickets</TableCell>
              <TableCell align="left">Status</TableCell>
            </TableRow>
          }
          empty={
            orgsQuery.data.data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6, color: "text.disabled" }}>
                  No organisations match this search.
                </TableCell>
              </TableRow>
            ) : null
          }
        >
          {orgsQuery.data.data.map((o) => (
            <TableRow
              key={o.id}
              sx={{
                borderTop: 1,
                borderColor: "divider",
                transition: "background-color 150ms",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <TableCell>
                <MuiLink
                  component={Link}
                  to={`/support/organizations/${o.id}`}
                  color="text.primary"
                  underline="hover"
                  sx={{ fontWeight: 500 }}
                >
                  {o.name}
                </MuiLink>
              </TableCell>
              <TableCell sx={{ color: "text.disabled" }}>
                {o.domain ?? "—"}
              </TableCell>
              <TableCell>{o.contacts_count ?? "—"}</TableCell>
              <TableCell>{o.open_tickets_count ?? 0}</TableCell>
              <TableCell>
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    borderRadius: 999,
                    bgcolor: "action.hover",
                    px: 1,
                    py: 0.25,
                    fontSize: 12,
                  }}
                >
                  {o.status ?? "active"}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </SupportTable>
      )}

      {orgsQuery.data?.meta && (
        <Pagination
          {...paginationPropsFromMeta(orgsQuery.data.meta)}
          perPage={Number(perPage)}
          entityNoun="organizations"
          onPageChange={(p) => setParam("page", String(p))}
          onPerPageChange={(n) => {
            const next = new URLSearchParams(params);
            next.set("per_page", String(n));
            next.delete("page");
            setParams(next, { replace: true });
          }}
        />
      )}
    </Box>
  );
}

function SearchInput({
  value,
  onCommit,
}: {
  value: string;
  onCommit: (v: string) => void;
}): JSX.Element {
  return (
    <TextField
      type="search"
      defaultValue={value}
      placeholder="Search organisations — name, domain, slug…"
      onKeyDown={(e) => {
        if (e.key === "Enter") onCommit((e.target as HTMLInputElement).value);
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Box component="svg" viewBox="0 0 16 16" aria-hidden sx={{ height: 16, width: 16, color: "text.disabled" }}>
              <path d="M11 11l3 3M7 12a5 5 0 100-10 5 5 0 000 10z" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
            </Box>
          </InputAdornment>
        ),
      }}
      sx={{ maxWidth: "32rem", "& .MuiOutlinedInput-root": { borderRadius: 999, bgcolor: "surfaceSoft" } }}
    />
  );
}
